import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import SetSubcategory from './SetSubcategory';
import { Form, Input, Button, Card, Dropdown } from 'semantic-ui-react';
import i18next from 'i18next';
import MenuSubcategory from './MenuSubcategory';
import MenuTaxSetup from './MenuTaxSetup';
import { DateRangePicker, ImageInput } from '../../common';
import { saveMenuItem, editEntityField, uploadImage } from '../../../actions';
import currencyOptions from '../../../consts/CommonCurrency.json';
import { getBusinessCurrency } from '../../../selectors';

class ItemEditor extends Component {
    state = { itemInstance: {} }

    componentDidUpdate(prevProps) {
        if (!prevProps.itemInstance && this.props.itemInstance) {
            this.setState({ itemInstance: {} });
        } else if (prevProps.itemInstance && !this.props.itemInstance) {
            this.setState({ itemInstance: {} });
        }
    }

    onDragEnd = (result) => {
        const { destination, source, draggableId, type } = result;

        if (!result.destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        if (type === 'subcategories') {
            const subcategoryId = draggableId.split('-')[1];

            const newSubcategoriesIds = Array.from(this.props.entity.subcategoriesIds);

            newSubcategoriesIds.splice(source.index, 1);
            newSubcategoriesIds.splice(destination.index, 0, subcategoryId);

            this.props.editEntityField('subcategoriesIds', newSubcategoriesIds);
        }
    }

    editItemInstance = (edits) => {
        this.setState({ itemInstanceDirty: true, itemInstance: { ...this.state.itemInstance, ...edits } });
    }

    handleSubmit = (event) => {
        const { menuId, ...item } = this.props.entity;

        if (item.price) item.price = Number(item.price);

        if (this.props.updateItemInstance && Object.keys(this.state.itemInstance).length) {
            const { itemInstance } = this.state;

            if (Object.keys(itemInstance).length) {
                itemInstance.id = this.props.itemInstance.id;
                if (itemInstance.price) itemInstance.price = Number(itemInstance.price);
                this.props.updateItemInstance(itemInstance);
            }
        }

        const updates = !item.id ? item :
            this.props.dirtyFields.concat('id').reduce((results, field) => ({ ...results, [field]: item[field] }), {});

        this.props.saveMenuItem(menuId, this.props.branchIdForOverride, updates, this.props.linkToCategory);
        event.preventDefault();
    }

    renderItemInstanceMiniForm = () => {
        if (this.props.itemInstance) {
            const { price, posItemId, meta } = { ...this.props.itemInstance, ...this.state.itemInstance };

            return (
                <Card fluid>
                    <Card.Content>
                        <Card.Header>{i18next.t('menu.overrideItem')}</Card.Header>
                    </Card.Content>
                    <Form.Group widths='equal' as={Card.Content} style={{ padding: 8, paddingLeft: 0, paddingRight: 0 }}>
                        <Form.Field>
                            <label>{i18next.t('menu.overridePosId')}</label>
                            <Input
                                data-lpignore="true"
                                fluid
                                value={posItemId || ''}
                                onChange={({ target }) => this.editItemInstance({ posItemId: target.value || null })}
                                placeholder={i18next.t('menu.overridePosId')}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{i18next.t('menu.overridePrice')}</label>
                            <Input
                                fluid
                                label={{ basic: true, content: currencyOptions[this.props.currencyCode].symbol_native }}
                                labelPosition='right'
                                value={price === undefined || price === null ? '' : String(price)}
                                onChange={({ target }) => this.editItemInstance({ price: target.value || null })}
                                placeholder={i18next.t('menu.overridePrice')}
                                disabled={this.props.entity.options.replaceMainItemWithItsSelections}
                            />
                        </Form.Field>
                    </Form.Group>
                    <Form.Group widths='equal' as={Card.Content} style={{ padding: 8, paddingLeft: 0, paddingRight: 0 }}>
                        <Form.Field>
                            <Form.Checkbox
                                label={i18next.t('menu.preSelected')}
                                checked={!!(meta || {}).preSelected}
                                onChange={(e, { checked }) => this.editItemInstance({ meta: { ...(meta || {}), preSelected: checked || undefined } })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Form.Checkbox
                                label={i18next.t('menu.isDiscount')}
                                checked={!!(meta || {}).isForDiscount}
                                onChange={(e, { checked }) => this.editItemInstance({ meta: { ...(meta || {}), isForDiscount: checked || undefined } })}
                            />
                        </Form.Field>
                    </Form.Group>
                </Card>
            );
        }
    }

    render() {
        if (!this.props.entity || this.props.entityType !== 'menuItem') {
            return null;
        }

        const item = this.props.entity || {};

        return (
            <Form onSubmit={this.handleSubmit}>
                {this.renderItemInstanceMiniForm()}

                <Form.Field>
                    <label>{i18next.t('posId')}</label>
                    <input
                        placeholder={i18next.t('posId')}
                        value={item.posItemId || ''}
                        onChange={({ target }) => this.props.editEntityField('posItemId', target.value)}
                        required
                    />
                </Form.Field>
                <Form.Field>
                    <label>{i18next.t('itemName')}</label>
                    <input
                        placeholder={i18next.t('itemName')}
                        value={item.name || ''}
                        onChange={({ target }) => this.props.editEntityField('name', target.value)}
                        required
                        autoFocus={window.innerWidth > 600}
                    />
                </Form.Field>

                <Form.TextArea
                    label={i18next.t('description')}
                    placeholder={i18next.t('description')}
                    value={item.description || ''}
                    onChange={({ target }) => this.props.editEntityField('description', target.value)}
                />

                <Form.Field>
                    <label>{i18next.t('menu.posItemName')}</label>
                    <input
                        placeholder={i18next.t('menu.posItemName')}
                        value={item.posName || ''}
                        onChange={({ target }) => this.props.editEntityField('posName', target.value)}
                    />
                </Form.Field>

                <Form.Group widths='equal'>
                    <Form.Field>
                        <label>{i18next.t('price')}</label>
                        <Input
                            fluid
                            label={{ basic: true, content: currencyOptions[this.props.currencyCode].symbol_native }}
                            labelPosition='right'
                            type='text'
                            placeholder={i18next.t('price')}
                            required
                            disabled={item.options.replaceMainItemWithItsSelections}
                            value={item.price === null ? '' : String(item.price)}
                            onChange={({ target }) => this.props.editEntityField('price', target.value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>{i18next.t('limit')}</label>
                        <Input
                            fluid
                            type='number'
                            min="0"
                            placeholder={i18next.t('limit')}
                            value={item.options.limit > 0 ? item.options.limit : ''}
                            onChange={({ target }) => {
                                if (target.value <= 9) {
                                    this.props.editEntityField('options.limit', target.value ? Number(target.value) : null);
                                }
                            }}
                        />
                    </Form.Field>
                </Form.Group>

                <Form.Group inline>
                    <label>{i18next.t('menu.availableFor')}</label>
                    <Form.Checkbox
                        label={i18next.t('sitting')}
                        checked={item.options.supportedFor.includes('sitting')}
                        onChange={(e, { checked }) => this.props.editEntityField('options.supportedFor', checked ? item.options.supportedFor.concat('sitting') : item.options.supportedFor.filter(s => s !== 'sitting'))}
                    />
                    <Form.Checkbox
                        label={i18next.t('takeaway')}
                        checked={item.options.supportedFor.includes('takeaway')}
                        onChange={(e, { checked }) => this.props.editEntityField('options.supportedFor', checked ? item.options.supportedFor.concat('takeaway') : item.options.supportedFor.filter(s => s !== 'takeaway'))}
                    />
                    <Form.Checkbox
                        label={i18next.t('delivery')}
                        checked={item.options.supportedFor.includes('delivery')}
                        onChange={(e, { checked }) => this.props.editEntityField('options.supportedFor', checked ? item.options.supportedFor.concat('delivery') : item.options.supportedFor.filter(s => s !== 'delivery'))}
                    />
                </Form.Group>

                <MenuTaxSetup entity={item} />

                <Form.Field>
                    <label>{i18next.t('menu.tags')}</label>
                    <Dropdown
                        placeholder={i18next.t('menu.tags')}
                        fluid
                        selection
                        multiple
                        options={['hotWeather', 'coldWeather'].map(value => ({ value, text: i18next.t(`tags.smartPromotions.${value}`) }))}
                        value={item.tags || []}
                        onChange={(e, { value }) => {
                            this.props.editEntityField('tags', value);
                        }}
                    />
                </Form.Field>

                <DragDropContext onDragEnd={this.onDragEnd}>
                    <Form.Field>
                        <SetSubcategory
                            onItemSelected={subcategory => this.props.editEntityField('subcategoriesIds', (item.subcategoriesIds || []).concat(subcategory.id))}

                        />
                        <Droppable droppableId={`item-${item.id}-subcategories`} type="subcategories">
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {item.subcategoriesIds.map((subId, index) => (
                                        <MenuSubcategory
                                            key={subId + index}
                                            parantId={item.id}
                                            category={this.props.categories[subId]}
                                            index={index}
                                            handleRemove={subcategoryId => {
                                                const index = item.subcategoriesIds.indexOf(subcategoryId);
                                                this.props.editEntityField('subcategoriesIds', (item.subcategoriesIds || []).filter((subId, i) => i !== index));
                                            }}

                                            preventSafeDelete
                                        />
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Form.Field>
                </DragDropContext>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('menu.enableThirdLevel')}
                        checked={item.options.enableThirdLevel}
                        onChange={(e, { checked }) => this.props.editEntityField('options.enableThirdLevel', checked)}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('menu.replaceMainItemWithItsSelections')}
                        checked={item.options.replaceMainItemWithItsSelections}
                        onChange={(e, { checked }) => {
                            this.props.editEntityField('options.replaceMainItemWithItsSelections', checked);
                            this.props.editEntityField('price', 0);
                            if (this.props.itemInstance) {
                                this.editItemInstance({ price: null });
                            }
                        }}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('image')}</label>
                    <ImageInput
                        resizeMode
                        handleFileUpload={(file, mode) => this.props.uploadImage(file, 'menuItem', mode)}
                        imageUrl={item.imageUrl}
                        handleCommonSelection={value => this.props.editEntityField('imageGuid', value)}
                        unsetImage={() => {
                            this.props.editEntityField('imageGuid', null);
                            this.props.editEntityField('imageUrl', null);
                        }}
                    />
                </Form.Field>

                <Form.Field>
                    <label>{i18next.t('menu.outOfStockDates')}</label>
                    <DateRangePicker
                        value={item.outOfStockDates ? [moment(item.outOfStockDates[0].startDate), moment(item.outOfStockDates[0].endDate)] : []}
                        onChange={(rangeDates) => this.props.editEntityField('outOfStockDates', [{ startDate: rangeDates[0].format('YYYY-MM-DD'), endDate: rangeDates[1].format('YYYY-MM-DD') }])}

                        enableFutureDate
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('suspended')}
                        checked={item.isDisabled}
                        onChange={(e, { checked }) => this.props.editEntityField('isDisabled', checked)}
                    />
                </Form.Field>

                <Button primary type='submit' floated="right">
                    {i18next.t('save')}
                </Button>
            </Form>
        );
    }
}

ItemEditor.propTypes = {
    currencyCode: PropTypes.string,
    editEntityField: PropTypes.func,
    categoriesOptions: PropTypes.array,
    itemInstance: PropTypes.object,
    linkToCategory: PropTypes.func,
    updateItemInstance: PropTypes.func,
    uploadImage: PropTypes.func,
    saveMenuItem: PropTypes.func,
    branchIdForOverride: PropTypes.string,
    entity: PropTypes.object,
    entityType: PropTypes.string,
    dirtyFields: PropTypes.array,

};

const mapStateToProps = (state) => {
    const { entity, type: entityType, extra, dirtyFields } = state.editor;
    const { categories } = state.businessApp.menu;
    const currencyCode = getBusinessCurrency(state);

    const categoriesOptions = Object.values(state.businessApp.menu.categories).filter(c => ['mandatory', 'addon'].includes(c.type)).map((c) => ({ value: c.id, text: `${c.key}-${c.name} ${c.note && `(${c.note})` || ''}` }));

    return { entity, entityType, ...(extra || {}), dirtyFields, categoriesOptions, categories, currencyCode };
};

export default connect(mapStateToProps, { saveMenuItem, editEntityField, uploadImage })(ItemEditor);
